"use client"

import { useState } from "react"
import { Flag, AlertTriangle } from "@/lib/icons"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group"
import { createClient } from "@/lib/supabase/client"
import { useAuthStore } from "@/lib/auth-store"
import { LoginPromptDialog } from "@/components/auth/login-prompt-dialog"
import { useTranslations } from "next-intl"

interface ReportContentDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  contentType: "script" | "tag" | "user" | "comment" | "contribution"
  contentId: string
  contentTitle?: string
}

const REPORT_REASONS = [
  { value: "spam", label: "Spam or misleading" },
  { value: "inappropriate", label: "Inappropriate content" },
  { value: "harassment", label: "Harassment or bullying" },
  { value: "copyright", label: "Copyright violation" },
  { value: "other", label: "Other" },
]

export function ReportContentDialog({
  open,
  onOpenChange,
  contentType,
  contentId,
  contentTitle,
}: ReportContentDialogProps) {
  const t = useTranslations("reportContentDialog")
  const { user, isGuest } = useAuthStore()
  const [reason, setReason] = useState("")
  const [details, setDetails] = useState("")
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [showLoginPrompt, setShowLoginPrompt] = useState(false)

  const handleSubmit = async () => {
    if (!reason) return

    if (isGuest || !user) {
      setShowLoginPrompt(true)
      return
    }

    setIsSubmitting(true)
    try {
      const supabase = createClient()
      // content_type (not flagged_content_type, which isn't a real column on this table) — this
      // insert was silently failing every single time before this fix.
      const { error } = await supabase.from("content_flags").insert({
        content_type: contentType,
        content_id: contentId,
        reason: `${reason}${details ? `: ${details}` : ""}`,
        flagged_by: user.id,
        status: "pending",
      })

      if (error) throw error

      setSubmitted(true)
      setTimeout(() => {
        onOpenChange(false)
        setSubmitted(false)
        setReason("")
        setDetails("")
      }, 2000)
    } catch (error) {
      console.error("Failed to submit report:", error)
    } finally {
      setIsSubmitting(false)
    }
  }

  if (submitted) {
    return (
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <div className="flex flex-col items-center justify-center py-8">
            <div className="h-12 w-12 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
              <Flag className="h-6 w-6 text-green-500" />
            </div>
            <h3 className="text-lg font-semibold">{t("reportSubmitted")}</h3>
            <p className="text-sm text-muted-foreground text-center mt-2">
              {t("thankYouForHelpingKeepOurCommunitySafeW")}
            </p>
          </div>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-yellow-500" />
              {t("reportContent")}
            </DialogTitle>
            <DialogDescription>
              {contentTitle ? (
                <>
                  {t("reporting")} <span className="font-medium">{contentTitle}</span>
                </>
              ) : (
                <>{t("reportThisTypeForReviewByOurModeration", { type: contentType })}</>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>{t("whyAreYouReportingThis")}</Label>
              <RadioGroup value={reason} onValueChange={setReason}>
                {REPORT_REASONS.map((r) => (
                  <div key={r.value} className="flex items-center space-x-2">
                    <RadioGroupItem value={r.value} id={r.value} />
                    <Label htmlFor={r.value} className="font-normal cursor-pointer">
                      {r.label}
                    </Label>
                  </div>
                ))}
              </RadioGroup>
            </div>

            <div className="space-y-2">
              <Label htmlFor="details">{t("additionalDetailsOptional")}</Label>
              <Textarea
                id="details"
                placeholder={t("provideAnyAdditionalContext")}
                value={details}
                onChange={(e) => setDetails(e.target.value)}
                className="resize-none"
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              {t("cancel")}
            </Button>
            <Button onClick={handleSubmit} disabled={!reason || isSubmitting} className="bg-red-600 hover:bg-red-700">
              {isSubmitting ? t("submitting") : t("submitReport")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <LoginPromptDialog open={showLoginPrompt} onOpenChange={setShowLoginPrompt} feature="report content" />
    </>
  )
}
